import { FC, MouseEvent, MouseEventHandler, ReactNode, useId, useRef } from 'react';
import { clsx } from 'clsx';
import Button, { type IButtonProps } from '../../atoms/Button';
import { ARMED_SHORT_TEXT, armedTextOf, pressAgainTextOf, textLabelOf } from '../../lib/doublePressText';
import { useDoublePress } from '../../hooks/useDoublePress';
import type { DropdownPosition } from '../../types/DropdownPosition';
import DoublePressFeedback, { type DoublePressTone } from './DoublePressFeedback';
import DoublePressLabel from './DoublePressLabel';
import './DoublePress.scss';

export interface IDoublePressButtonProps extends IButtonProps {
    /** Вопрос взведённой кнопки — подсказкой и именем кнопки (на ней самой — «Точно?»); по умолчанию «Точно <подпись>?». */
    armedText?: ReactNode;
    /** Вопрос — скринридер объявляет его при взводе. */
    confirmationText?: ReactNode;
    /** Пояснение у взведённой кнопки (подсказкой): то, чего нет на кнопке, — число записей, необратимость. */
    confirmationHint?: ReactNode;
    armedTone?: DoublePressTone;
    /** Где подсказка взведённой кнопки. @default bottom */
    hintPosition?: DropdownPosition;
    /** Сколько ждёт второе нажатие, мс. */
    timeout?: number;
    /** Пояснение грузится: второе нажатие не выполняется. */
    hintLoading?: boolean;
    /** Первое нажатие — кнопка взведена. */
    onArm?: MouseEventHandler;
    onArmedChange?: (armed: boolean) => void;
}

const isEmpty = (node: ReactNode) => node === undefined || node === null || node === false || node === '';

/** Кнопка пакета в режиме «нажми дважды» (`ButtonWithConfirm mode="double"`). */
const DoublePressButton: FC<IDoublePressButtonProps> = ({
    armedText,
    confirmationText,
    confirmationHint,
    armedTone = 'primary',
    hintPosition = 'bottom',
    timeout,
    hintLoading = false,
    onArm,
    onArmedChange,
    onClick,
    children,
    ...props
}) => {
    const {
        type,
        form,
        disabled,
        loading,
        className,
        title,
        onBlur,
        onKeyDown,
        'aria-label': ariaLabel,
        'aria-describedby': describedBy,
        ...rest
    } = props;
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    const hintId = useId();
    const label = textLabelOf(children, ariaLabel, title);
    const armedLabel = armedText ?? armedTextOf(label);
    const iconOnly = isEmpty(children);
    const hasHint = !isEmpty(confirmationHint);

    // submit — только вторым нажатием (первое лишь взводит кнопку) и, как у нативной кнопки, после onClick
    const confirm = (event: MouseEvent<HTMLButtonElement>) => {
        const result = onClick?.(event);
        if (type === 'submit' && !event.defaultPrevented) {
            const owner = form ? document.getElementById(form) : event.currentTarget.form;
            if (owner instanceof HTMLFormElement) owner.requestSubmit();
        }
        return result;
    };
    const press = useDoublePress<HTMLButtonElement, HTMLDivElement>({
        onConfirm: confirm,
        onArm,
        disabled: !!disabled,
        busy: hintLoading,
        timeout,
        onArmedChange,
    });
    const armed = press.armed;
    const armedName = armed ? textLabelOf(armedLabel) : undefined;
    const announcement = (
        <>
            {!isEmpty(confirmationText) && <span>{confirmationText} </span>}
            {hasHint && <span>{confirmationHint} </span>}
            {pressAgainTextOf(label)}
        </>
    );

    return (
        <div ref={press.rootRef} className="rf-button-confirm rf-double-press" data-armed={armed || undefined}>
            <Button
                {...rest}
                ref={buttonRef}
                type="button"
                className={clsx(
                    className,
                    armed && 'rf-double-press--armed',
                    armed && armedTone === 'danger' && 'rf-double-press--danger',
                )}
                title={armed ? undefined : title}
                aria-label={armedName ?? ariaLabel}
                aria-describedby={clsx(describedBy, armed && hasHint && hintId) || undefined}
                disabled={disabled || press.pending}
                loading={loading || press.pending}
                onClick={press.triggerProps.onClick}
                onBlur={(event) => {
                    onBlur?.(event);
                    press.triggerProps.onBlur(event);
                }}
                onKeyDown={(event) => {
                    onKeyDown?.(event);
                    press.triggerProps.onKeyDown(event);
                }}
            >
                {iconOnly ? null : (
                    <DoublePressLabel armed={armed} armedText={ARMED_SHORT_TEXT}>
                        {children}
                    </DoublePressLabel>
                )}
            </Button>
            <DoublePressFeedback
                armed={armed}
                target={buttonRef}
                announcement={announcement}
                title={armedLabel}
                hint={confirmationHint}
                hintId={hintId}
                busy={hintLoading}
                tone={armedTone}
                position={hintPosition}
            />
        </div>
    );
};

export default DoublePressButton;
